import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WaveSurfer from 'wavesurfer.js';
import AudioPlayer from '../../components/learning/AudioPlayer';
import PronunciationResultPanel from '../../components/learning/PronunciationResultPanel';
import CoachingPanel from '../../components/learning/CoachingPanel';
import WordPracticeModal, { WordPracticeResult } from '../../components/learning/WordPracticeModal';
import { stripWordPunctuation } from '../../utils/wordComparison';
import { useLearningContext } from './LearningLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { useLearningSession } from '../../hooks/useLearningSession';
import { useShadowingMode } from '../../hooks/useShadowingMode';
import { pronunciationService, PronunciationResult } from '../../services/pronunciationService';
import { learningService } from '../../services/learningService';
import { dictionaryService } from '../../services/dictionaryService';
import type { ModeProgressSummary } from '../../types/learning';
import { ClickableText } from '../../components/ClickableWord';
import { AnnotatedChunks } from '../../components/learning/AnnotatedChunks';
import DictationGuidancePanel, { GuidanceItem } from '../../components/learning/DictationGuidancePanel';
import { getTranslation } from '../../utils/getTranslation';
import './ShadowingPage.css';
import './SentenceNav.css';

import { resolveMediaUrl } from '../../utils/mediaUrl';
import XpToastContainer, { useXpToast } from '../../components/learning/XpToast';
import HeadphoneCheckModal from '../../components/shadowing/HeadphoneCheckModal';
import LeakDetectedModal from '../../components/shadowing/LeakDetectedModal';
import { detectHeadphone, HeadphoneStatus } from '../../utils/audioDevices';
import { useEchoLeakDetection } from '../../hooks/useEchoLeakDetection';

// Scoring weights for pronunciation assessment
const SCORE_WEIGHTS = {
  accuracy: 0.35,
  fluency: 0.25,
  completeness: 0.25,
  prosody: 0.15,
};

const PROFESSIONAL_SCORE_WEIGHTS = {
  accuracy: 0.40,
  fluency: 0.30,
  completeness: 0.30,
};

// Silence detection constants for professional mode
const SILENCE_THRESHOLD = 0.01;
const SILENCE_DURATION_MS = 1000;
const MAX_RECORDING_MS = 15000;

// Calculate weighted score from individual components
const calculateWeightedScore = (accuracy: number, fluency: number, completeness: number, prosody: number): number => {
  return Math.round(
    accuracy * SCORE_WEIGHTS.accuracy +
    fluency * SCORE_WEIGHTS.fluency +
    completeness * SCORE_WEIGHTS.completeness +
    prosody * SCORE_WEIGHTS.prosody
  );
};

const calculateProfessionalScore = (accuracy: number, fluency: number, completeness: number): number => {
  return Math.round(
    accuracy * PROFESSIONAL_SCORE_WEIGHTS.accuracy +
    fluency * PROFESSIONAL_SCORE_WEIGHTS.fluency +
    completeness * PROFESSIONAL_SCORE_WEIGHTS.completeness
  );
};

interface ChunkItem {
  text: string;
  meaning: string;
  annotations?: Array<{ word: string; type: 'stress' | 'link' | 'pause' | 'reduction' | 'ipa' | 'syllables'; hint?: string }>;
  intonation?: 'rising' | 'falling' | null;
  pause?: boolean;
}

interface Segment {
  id: string;
  text: string;
  translation: string;
  audioUrl?: string;
  chunks: ChunkItem[];
  speakingTip: string;
  tone?: string;
}

interface WordPracticeState {
  word: string;
  originalScore: number;
  wordIndex: number;
}

const ShadowingPage: React.FC = () => {
  const { t } = useTranslation('learning');
  const { courseId } = useParams<{ courseId: string }>();
  // openNoteWithContent was wired to the removed per-word "+" button
  // — keep it pulled to avoid an unused-destructure warning, but
  // it's no longer rendered. Re-add when (if) we restore the
  // suggestion block.
  const { courseData, loading, error } = useLearningContext();
  const { user } = useAuth();
  const { toasts: xpToasts, showXp } = useXpToast();
  const userLang = user?.languagePreference || courseData?.course?.translationLanguage || 'zh-TW';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [audioSpeed, setAudioSpeed] = useState<number | undefined>(undefined);
  const [audioRepeating, setAudioRepeating] = useState(false);
  const [isAssessing, setIsAssessing] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState<PronunciationResult | null>(null);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [serviceConfigured, setServiceConfigured] = useState<boolean | null>(null);

  // Word re-practice state
  const [wordPracticeMode, setWordPracticeMode] = useState<WordPracticeState | null>(null);
  const [isAssessingWord, setIsAssessingWord] = useState(false);
  const [updatedWordScores, setUpdatedWordScores] = useState<Map<number, number>>(new Map());
  const [wordPhonetic, setWordPhonetic] = useState<string | null>(null);
  const [wordAudioUrl, setWordAudioUrl] = useState<string | null>(null);
  const [isPlayingWordAudio, setIsPlayingWordAudio] = useState(false);
  // Drives the modal's before → after view; null = pre-record / record state.
  const [wordPracticeResult, setWordPracticeResult] = useState<WordPracticeResult | null>(null);
  const wordAudioRef = useRef<HTMLAudioElement | null>(null);

  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const [, setIsPlayingRecording] = useState(false);

  // Progress tracking (for resume and free selection)
  const [progressSummary, setProgressSummary] = useState<ModeProgressSummary | null>(null);
  const [hasLoadedInitialPosition, setHasLoadedInitialPosition] = useState(false);
  const [showSentenceSelector, setShowSentenceSelector] = useState(false);
  const [showCourseSummary, setShowCourseSummary] = useState(false);
  const savePositionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Professional shadowing mode
  const {
    isProfessionalMode, isProfessionalAvailable, toggleMode: toggleProfessionalMode,
    speakingScore, speakingCefr, unlockScore, unlockCefr,
    hasSeenHeadphoneTip, dismissHeadphoneTip,
    hasDismissedLockedPrompt, dismissLockedPrompt,
  } = useShadowingMode();
  const [scriptVisible, setScriptVisible] = useState(true);
  const [guidance, setGuidance] = useState<GuidanceItem[]>([]);

  // Professional mode refs
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const wavesurferContainerRef = useRef<HTMLDivElement | null>(null);
  const micCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const maxRecordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioFinishedRef = useRef(false);
  const silenceStartRef = useRef(0);
  const playbackStartedRef = useRef(false);
  const professionalRecordingRef = useRef(false);

  // Headphone preflight check (Pro mode only)
  const [hpCheckOpen, setHpCheckOpen] = useState(false);
  const [hpStatus, setHpStatus] = useState<HeadphoneStatus>('unknown');
  const [hpLabel, setHpLabel] = useState<string | undefined>(undefined);

  // Echo-leak runtime detection (Pro mode only)
  const [leakModalOpen, setLeakModalOpen] = useState(false);

  // Learning session tracking (time spent in this mode)
  const { updateContext: updateSessionContext } = useLearningSession({
    courseId: courseId || '',
    mode: 'shadowing',
    enabled: !!courseId && !!user,
    contextData: { sentenceIndex: currentIndex },
  });

  // Update session context when sentence index changes
  useEffect(() => {
    updateSessionContext({ sentenceIndex: currentIndex });
  }, [currentIndex, updateSessionContext]);

  const {
    isRecording,
    audioBlob,
    startRecording,
    stopRecording,
    clearRecording,
    error: recordingError,
    analyserNode,
    getRecentMicPcm,
    getSampleRate,
  } = useAudioRecorder({
    audioConstraints: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });

  // Check if Azure Speech is configured
  useEffect(() => {
    pronunciationService.getStatus()
      .then((status) => setServiceConfigured(status.configured))
      .catch(() => setServiceConfigured(false));
  }, []);

  // Refs to store latest handler functions (to avoid stale closures in useEffect)
  const handleAssessRef = useRef<() => Promise<void>>();
  const handleAssessWordRef = useRef<() => Promise<void>>();

  // Auto-assess when recording stops (audioBlob is set) - for sentence
  const hasTriggeredAssessment = useRef(false);
  useEffect(() => {
    // Only trigger if we have a new audioBlob and haven't already triggered (sentence mode)
    if (audioBlob && !isRecording && !isAssessing && !wordPracticeMode && serviceConfigured && !hasTriggeredAssessment.current) {
      hasTriggeredAssessment.current = true;
      // Small delay to ensure audio is ready
      const timer = setTimeout(() => {
        handleAssessRef.current?.();
      }, 100);
      return () => clearTimeout(timer);
    }
    // Reset flag when audioBlob is cleared
    if (!audioBlob) {
      hasTriggeredAssessment.current = false;
    }
  }, [audioBlob, isRecording, isAssessing, wordPracticeMode, serviceConfigured]);

  // Auto-assess when recording stops - for word practice
  const hasTriggeredWordAssessment = useRef(false);
  useEffect(() => {
    // Only trigger if we have a new audioBlob in word practice mode
    if (audioBlob && !isRecording && !isAssessingWord && wordPracticeMode && serviceConfigured && !hasTriggeredWordAssessment.current) {
      hasTriggeredWordAssessment.current = true;
      const timer = setTimeout(() => {
        handleAssessWordRef.current?.();
      }, 100);
      return () => clearTimeout(timer);
    }
    // Reset flag when audioBlob is cleared or word practice mode ends
    if (!audioBlob || !wordPracticeMode) {
      hasTriggeredWordAssessment.current = false;
    }
  }, [audioBlob, isRecording, isAssessingWord, wordPracticeMode, serviceConfigured]);

  // Convert sentences from courseData to segments
  const segments: Segment[] = useMemo(() => {
    if (!courseData?.sentences) return [];

    return courseData.sentences.map((sentence, index) => {
      const audioUrl = resolveMediaUrl(sentence.audio_url) || undefined;

      // Extract chunks from speaking_strategy
      const chunks: ChunkItem[] = ((sentence as any).speaking_strategy?.chunks || []).map((chunk: any) => ({
        text: chunk.text || chunk.en || '',
        meaning: getTranslation(chunk.translations, userLang, chunk.translation || chunk.meaning || chunk.zh_tw || ''),
        annotations: chunk.annotations || undefined,
        intonation: chunk.intonation || undefined,
        pause: chunk.pause || undefined,
      }));

      return {
        id: String(index),
        text: sentence.text,
        translation: getTranslation((sentence as any).translations, userLang, sentence.translation || ''),
        audioUrl: audioUrl || undefined,
        chunks,
        speakingTip: getTranslation((sentence as any).speaking_strategy?.tips_natives, userLang, (sentence as any).speaking_strategy?.tips_native || ''),
        tone: (sentence as any).speaking_strategy?.tone || undefined,
      };
    });
  }, [courseData?.sentences]);

  const currentSegment = segments[currentIndex];

  // Calculate current sentence's best score
  const currentSentenceBestScore = useMemo(() => {
    if (!progressSummary?.sentenceScores) return undefined;
    const scoreEntry = progressSummary.sentenceScores.find(s => s.sentenceIndex === currentIndex);
    return scoreEntry?.score;
  }, [progressSummary?.sentenceScores, currentIndex]);

  // Load progress summary and set initial position
  useEffect(() => {
    if (!courseId || hasLoadedInitialPosition) return;

    const loadProgress = async () => {
      try {
        const summary = await learningService.getProgressSummary(courseId);
        setProgressSummary(summary.shadowing);

        // Set initial position: resume from last position or first incomplete
        if (summary.shadowing.currentIndex !== undefined && summary.shadowing.currentIndex < segments.length) {
          setCurrentIndex(summary.shadowing.currentIndex);
        }

        // Show sentence selector if all sentences have been completed once
        if (summary.shadowing.isCompleted) {
          setShowSentenceSelector(true);
        }
      } catch (err) {
        console.error('Failed to load progress summary:', err);
      } finally {
        setHasLoadedInitialPosition(true);
      }
    };

    if (segments.length > 0) {
      loadProgress();
    }
  }, [courseId, segments.length, hasLoadedInitialPosition]);

  // Save position when changing sentences (debounced)
  useEffect(() => {
    if (!courseId || !hasLoadedInitialPosition) return;

    // Clear any pending save
    if (savePositionTimeoutRef.current) {
      clearTimeout(savePositionTimeoutRef.current);
    }

    // Debounce: save position after 1 second of no changes
    savePositionTimeoutRef.current = setTimeout(async () => {
      try {
        await learningService.savePosition(courseId, 'shadowing', currentIndex);
      } catch (err) {
        console.error('Failed to save position:', err);
      }
    }, 1000);

    return () => {
      if (savePositionTimeoutRef.current) {
        clearTimeout(savePositionTimeoutRef.current);
      }
    };
  }, [courseId, currentIndex, hasLoadedInitialPosition]);

  // Handle assessment
  const handleAssess = async () => {
    if (!audioBlob || !currentSegment) return;

    setIsAssessing(true);
    setAssessmentError(null);
    setAssessmentResult(null);
    setUpdatedWordScores(new Map()); // Reset word practice scores

    try {
      const result = await pronunciationService.assessPronunciation(
        audioBlob,
        currentSegment.text,
        {
          // Progressive UI: the assess promise resolves with scores
          // (~2 s); when the background /coach call finishes we
          // splice the result into state so <CoachingPanel> rerenders
          // with the new advice.
          onCoachReady: coach => {
            setAssessmentResult(prev => prev ? { ...prev, coach } : prev);
          },
        },
      );

      // Calculate weighted score — professional mode excludes prosody
      const weightedScore = isProfessionalMode
        ? calculateProfessionalScore(result.accuracyScore, result.fluencyScore, result.completenessScore)
        : calculateWeightedScore(result.accuracyScore, result.fluencyScore, result.completenessScore, result.prosodyScore);

      // Update result with weighted score
      const resultWithWeightedScore = {
        ...result,
        overallScore: weightedScore,
      };

      setAssessmentResult(resultWithWeightedScore);

      // Save mastery results to API
      if (courseId && result.words.length > 0) {
        try {
          // Fire-and-forget: upload recording for storage
          let recordingMeta: { url: string; size: number; durationMs: number | null } | null = null;
          if (audioBlob) {
            learningService.uploadShadowingRecording(audioBlob, {
              courseId,
              sentenceIndex: currentIndex,
            }).then(meta => {
              recordingMeta = meta;
              console.log('Recording uploaded:', meta.url);
            }).catch(err => console.warn('Recording upload failed (non-critical):', err));
          }

          const saveResult = await learningService.saveShadowingResults({
            courseId,
            sentenceIndex: currentIndex,
            sentenceText: currentSegment.text,
            overallScore: weightedScore,
            accuracyScore: result.accuracyScore,
            fluencyScore: result.fluencyScore,
            completenessScore: result.completenessScore,
            prosodyScore: result.prosodyScore,
            recognizedText: result.recognizedText,
            mode: isProfessionalMode ? 'shadowing' : 'echoing',
            scriptVisible,
            words: result.words.map(w => ({
              word: w.word,
              accuracyScore: w.accuracyScore,
              errorType: w.errorType,
              phonemes: w.phonemes,
            })),
          });
          console.log('Shadowing mastery saved successfully');
          if (saveResult?.guidance?.length > 0) {
            setGuidance(saveResult.guidance as GuidanceItem[]);
          }

          // Also call completeSentence API to track progress and update best score
          try {
            const completionResult = await learningService.completeSentence(
              courseId,
              'shadowing',
              currentIndex,
              weightedScore,
              segments.length
            );

            // Show XP toast
            if (completionResult.xpEarned > 0) {
              showXp(completionResult.xpEarned, completionResult.leveledUp, completionResult.newLevel);
            }

            // Update local progress summary
            if (completionResult.isNewBestScore || completionResult.isNewOverallBest) {
              const updatedSummary = await learningService.getProgressSummary(courseId);
              setProgressSummary(updatedSummary.shadowing);

              // If all sentences are now completed, show the sentence selector
              if (updatedSummary.shadowing.isCompleted) {
                setShowSentenceSelector(true);
              }
            }
          } catch (progressErr) {
            console.error('Failed to save sentence progress:', progressErr);
          }
        } catch (saveErr) {
          console.error('Failed to save mastery:', saveErr);
          // Don't show error to user - mastery save is non-critical
        }
      }
    } catch (err) {
      console.error('Assessment error:', err);
      setAssessmentError(err instanceof Error ? err.message : 'Assessment failed');
    } finally {
      setIsAssessing(false);
    }
  };

  // Keep ref updated with latest handleAssess
  handleAssessRef.current = handleAssess;

  // Handle word re-practice: record and assess a single word
  const handleWordPractice = async (word: string, wordIndex: number) => {
    // Clear any existing recording first to prevent auto-assess from triggering
    clearRecording();
    hasTriggeredWordAssessment.current = false;

    // Diff tokenizer keeps trailing punctuation ("ten.", "anecdotes.").
    // Strip before storing/looking up/assessing so the modal headline
    // and the per-word ASR reference both see the lexical form. Falls
    // back to the raw token only if cleaning would empty the string.
    const clean = stripWordPunctuation(word) || word;

    setWordPracticeMode({ word: clean, originalScore: assessmentResult?.words[wordIndex]?.accuracyScore || 0, wordIndex });
    setWordPhonetic(null);
    setWordAudioUrl(null);

    // Fetch pronunciation from dictionary
    try {
      const dictResult = await dictionaryService.lookup(clean, { generateAudio: true });
      if (dictResult.phonetic) {
        setWordPhonetic(dictResult.phonetic);
      }
      // Get audio URL if available
      if (dictResult.audio && dictResult.audio.length > 0) {
        setWordAudioUrl(dictResult.audio[0].url);
      }
    } catch (err) {
      console.log('Dictionary lookup failed for word:', clean, err);
      // Don't show error - phonetic is optional
    }
  };

  // Play word pronunciation from dictionary.
  //
  // Chrome aborts `play()` if `.src` was just assigned and the file
  // hasn't finished loading — the returned Promise rejects with
  // "interrupted by a new load request". The previous version of
  // this handler set `isPlayingWordAudio = true` synchronously after
  // calling `play()` regardless, so the button flipped to the "stop"
  // icon even when no audio was actually playing. Now we only flip
  // the flag once the Promise resolves (and reset it on rejection).
  const handlePlayWordAudio = () => {
    if (!wordAudioUrl || !wordAudioRef.current) return;
    const el = wordAudioRef.current;

    if (isPlayingWordAudio) {
      el.pause();
      el.currentTime = 0;
      setIsPlayingWordAudio(false);
    } else {
      el.src = wordAudioUrl;
      el.currentTime = 0;
      el.play()
        .then(() => setIsPlayingWordAudio(true))
        .catch(err => {
          console.warn('Word audio play failed:', err);
          setIsPlayingWordAudio(false);
        });
    }
  };

  // Assess word after recording
  const handleAssessWord = async () => {
    if (!audioBlob || !wordPracticeMode) return;

    setIsAssessingWord(true);

    try {
      const result = await pronunciationService.assessPronunciation(
        audioBlob,
        wordPracticeMode.word
      );

      // Get the word score from result (should be just one word)
      const wordScore = result.words.length > 0 ? result.words[0].accuracyScore : result.accuracyScore;

      // Update the word score in our map
      setUpdatedWordScores(prev => {
        const newMap = new Map(prev);
        newMap.set(wordPracticeMode.wordIndex, wordScore);
        return newMap;
      });

      // Capture the per-word score the learner just beat — used by
      // the modal's before → after view at the end of this handler.
      const wordBefore = assessmentResult?.words[wordPracticeMode.wordIndex]?.accuracyScore
        ?? wordPracticeMode.originalScore
        ?? 0;
      const overallBefore = assessmentResult?.overallScore ?? 0;
      const accuracyBefore = assessmentResult?.accuracyScore ?? 0;

      // Calculate new overall score with updated word scores
      if (assessmentResult) {
        const updatedWords = assessmentResult.words.map((w, idx) => {
          if (idx === wordPracticeMode.wordIndex) {
            return { ...w, accuracyScore: wordScore };
          }
          const updatedScore = updatedWordScores.get(idx);
          if (updatedScore !== undefined) {
            return { ...w, accuracyScore: updatedScore };
          }
          return w;
        });

        // Calculate new average word accuracy
        const avgWordAccuracy = updatedWords.reduce((sum, w) => sum + w.accuracyScore, 0) / updatedWords.length;

        // Recalculate weighted score with improved accuracy
        const newWeightedScore = calculateWeightedScore(
          avgWordAccuracy,
          assessmentResult.fluencyScore,
          assessmentResult.completenessScore,
          assessmentResult.prosodyScore
        );

        // Update the assessment result
        setAssessmentResult({
          ...assessmentResult,
          words: updatedWords,
          accuracyScore: avgWordAccuracy,
          overallScore: newWeightedScore,
        });

        // Hand the modal a before → after snapshot so it stays open
        // and shows the deltas instead of auto-closing. Learner
        // chooses 「再試一次」 to retry without closing or 「完成」
        // to dismiss.
        setWordPracticeResult({
          wordBefore: Math.round(wordBefore),
          wordAfter: Math.round(wordScore),
          accuracyBefore: Math.round(accuracyBefore),
          accuracyAfter: Math.round(avgWordAccuracy),
          overallBefore: Math.round(overallBefore),
          overallAfter: Math.round(newWeightedScore),
        });

        // Save improved score to API if it's better
        if (courseId && newWeightedScore > (currentSentenceBestScore || 0)) {
          try {
            const rePracticeResult = await learningService.completeSentence(
              courseId,
              'shadowing',
              currentIndex,
              newWeightedScore,
              segments.length
            );
            if (rePracticeResult.xpEarned > 0) {
              showXp(rePracticeResult.xpEarned, rePracticeResult.leveledUp, rePracticeResult.newLevel);
            }
            if (rePracticeResult.isNewBestScore) {
              const updatedSummary = await learningService.getProgressSummary(courseId);
              setProgressSummary(updatedSummary.shadowing);
            }
          } catch (err) {
            console.error('Failed to update score:', err);
          }
        }
      }

      // Modal stays open: it switches to the result view via
      // `wordPracticeResult` set above. Dismissed via 「完成」/X
      // (handleCancelWordPractice) or retried in place via
      // 「再試一次」 (handleRetryWordPractice).
      clearRecording();
    } catch (err) {
      console.error('Word assessment error:', err);
      setAssessmentError(err instanceof Error ? err.message : 'Word assessment failed');
    } finally {
      setIsAssessingWord(false);
    }
  };

  // Keep ref updated with latest handleAssessWord
  handleAssessWordRef.current = handleAssessWord;

  // Cancel word practice mode
  const handleCancelWordPractice = () => {
    setWordPracticeMode(null);
    setWordPhonetic(null);
    setWordAudioUrl(null);
    setIsPlayingWordAudio(false);
    setWordPracticeResult(null);
    clearRecording();
  };

  // 「再試一次」 inside the modal — clear the result view and the
  // recorder, but keep the modal open so the learner can take
  // another shot without re-clicking the chip.
  const handleRetryWordPractice = () => {
    setWordPracticeResult(null);
    hasTriggeredWordAssessment.current = false;
    clearRecording();
  };

  const resetState = useCallback(() => {
    clearRecording();
    setAssessmentResult(null);
    setAssessmentError(null);
    setIsPlayingRecording(false);
    setWordPracticeMode(null);
    setWordPracticeResult(null);
    setUpdatedWordScores(new Map());
    setGuidance([]);
  }, [clearRecording]);

  const handleNext = useCallback(() => {
    if (currentIndex < segments.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetState();
      // Scroll to top of page for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Last sentence - show course summary
      setShowCourseSummary(true);
    }
  }, [currentIndex, segments.length, resetState]);

  const isLastSentence = currentIndex === segments.length - 1;


  // Jump to a specific sentence (for free selection mode)
  const handleJumpToSentence = useCallback((index: number) => {
    setCurrentIndex(index);
    resetState();
    // Scroll to top of page for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [resetState]);

  // ── Echo-leak detector setup (Pro mode only) ──────────────
  // We need stopProfessionalRecording -> leakDetector.stop, AND
  // leakDetector -> stopProfessionalRecording (on leak fired). Break the cycle
  // by hoisting each side behind a ref.
  const leakDetectorStopRef = useRef<(() => void) | null>(null);
  const stopProRef = useRef<(() => void) | null>(null);

  // ── Professional mode: mic waveform + silence detection ──
  const stopProfessionalRecording = useCallback(() => {
    if (maxRecordingTimerRef.current) {
      clearTimeout(maxRecordingTimerRef.current);
      maxRecordingTimerRef.current = null;
    }
    if (wavesurferRef.current) {
      wavesurferRef.current.pause();
    }
    cancelAnimationFrame(animFrameRef.current);
    stopRecording();
    leakDetectorStopRef.current?.();
    professionalRecordingRef.current = false;
  }, [stopRecording]);
  stopProRef.current = stopProfessionalRecording;

  const resolvedAudioUrl = useMemo(
    () => (currentSegment?.audioUrl ? resolveMediaUrl(currentSegment.audioUrl) : null),
    [currentSegment?.audioUrl],
  );
  const handleLeakDetected = useCallback(() => {
    stopProRef.current?.();
    setLeakModalOpen(true);
  }, []);
  const leakDetector = useEchoLeakDetection({
    audioUrl: resolvedAudioUrl,
    getMicPcm: getRecentMicPcm,
    getMicSampleRate: getSampleRate,
    onLeakDetected: handleLeakDetected,
  });
  leakDetectorStopRef.current = leakDetector.stop;

  const drawMicWaveform = useCallback(() => {
    const canvas = micCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const analyser = analyserNode;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      // RMS calculation
      let sumSquares = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = (dataArray[i] - 128) / 128.0;
        sumSquares += v * v;
      }
      const rms = Math.sqrt(sumSquares / bufferLength);

      // Track playback state
      if (!playbackStartedRef.current && wavesurferRef.current?.isPlaying()) {
        playbackStartedRef.current = true;
      }
      if (!audioFinishedRef.current && playbackStartedRef.current && wavesurferRef.current && !wavesurferRef.current.isPlaying()) {
        audioFinishedRef.current = true;
      }

      // Silence detection after audio finishes
      if (audioFinishedRef.current) {
        if (rms < SILENCE_THRESHOLD) {
          if (silenceStartRef.current === 0) {
            silenceStartRef.current = performance.now();
          } else if (performance.now() - silenceStartRef.current >= SILENCE_DURATION_MS) {
            stopProfessionalRecording();
            return;
          }
        } else {
          silenceStartRef.current = 0;
        }
      }

      // Draw waveform
      ctx.fillStyle = '#052e16';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.shadowColor = '#4ade80';
      ctx.shadowBlur = 6;
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#4ade80';
      ctx.beginPath();
      const amplify = 2.5;
      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const raw = (dataArray[i] - 128) / 128.0;
        const amplified = Math.max(-1, Math.min(1, raw * amplify));
        const y = ((amplified + 1) / 2) * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    };

    draw();
  }, [analyserNode, stopProfessionalRecording]);

  const startProfessionalShadowing = useCallback(async () => {
    if (!currentSegment?.audioUrl) return;

    clearRecording();
    setAssessmentResult(null);
    setAssessmentError(null);
    audioFinishedRef.current = false;
    silenceStartRef.current = 0;
    playbackStartedRef.current = false;
    professionalRecordingRef.current = true;

    try {
      // Start recording first (before audio plays)
      await startRecording();

      // Init WaveSurfer for system audio visualization
      if (wavesurferContainerRef.current) {
        if (wavesurferRef.current) {
          wavesurferRef.current.destroy();
        }
        const ws = WaveSurfer.create({
          container: wavesurferContainerRef.current,
          waveColor: '#667eea',
          progressColor: '#4f46e5',
          cursorColor: '#fff',
          cursorWidth: 2,
          height: 80,
          barWidth: 2,
          barGap: 1,
          barRadius: 2,
          normalize: true,
          backend: 'WebAudio',
        });
        ws.load(resolveMediaUrl(currentSegment.audioUrl) || '');
        wavesurferRef.current = ws;

        ws.on('finish', () => {
          audioFinishedRef.current = true;
        });

        ws.on('ready', () => {
          ws.play();
          // Begin echo-leak detection from the moment playback starts.
          leakDetector.start(performance.now());
        });
      }

      // Set canvas size for mic waveform
      const canvas = micCanvasRef.current;
      if (canvas) {
        canvas.width = canvas.offsetWidth * window.devicePixelRatio;
        canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      }
      drawMicWaveform();

      // Safety timer
      maxRecordingTimerRef.current = setTimeout(() => {
        stopProfessionalRecording();
      }, MAX_RECORDING_MS);
    } catch (err) {
      console.error('Failed to start professional shadowing:', err);
      setAssessmentError(err instanceof Error ? err.message : 'Failed to start');
    }
  }, [currentSegment?.audioUrl, clearRecording, startRecording, drawMicWaveform, stopProfessionalRecording, leakDetector]);

  // Pre-launch headphone check for Pro mode
  const requestProfessionalShadowing = useCallback(async () => {
    const result = await detectHeadphone();
    setHpStatus(result.status);
    setHpLabel(result.label);
    setHpCheckOpen(true);
  }, []);

  const handleHpConfirm = useCallback(() => {
    setHpCheckOpen(false);
    startProfessionalShadowing();
  }, [startProfessionalShadowing]);

  const handleHpCancel = useCallback(() => {
    setHpCheckOpen(false);
  }, []);

  const handleLeakRestart = useCallback(() => {
    setLeakModalOpen(false);
    // Restart same sentence from the top (recording cleared inside startProfessionalShadowing)
    startProfessionalShadowing();
  }, [startProfessionalShadowing]);

  const handleLeakCancel = useCallback(() => {
    setLeakModalOpen(false);
  }, []);

  // Cleanup WaveSurfer on unmount or sentence change
  useEffect(() => {
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
      cancelAnimationFrame(animFrameRef.current);
      if (maxRecordingTimerRef.current) {
        clearTimeout(maxRecordingTimerRef.current);
      }
    };
  }, [currentIndex]);

  // Show loading state
  if (loading) {
    return (
      <div className="shadowing-page">
        <div className="shadowing-loading">{t('shadowing.loading')}</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="shadowing-page">
        <div className="shadowing-error">
          <h3>{t('shadowing.errorTitle')}</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No sentences available
  if (segments.length === 0) {
    return (
      <div className="shadowing-page">
        <div className="shadowing-empty">
          <h3>{t('shadowing.noSentencesTitle')}</h3>
          <p>{t('shadowing.noSentencesDesc')}</p>
        </div>
      </div>
    );
  }

  if (!currentSegment) {
    return (
      <div className="shadowing-page">
        <div className="shadowing-loading">{t('shadowing.loadingSimple')}</div>
      </div>
    );
  }

  return (
    <div className="shadowing-page">
      {/* Hidden audio elements for playback */}
      <audio
        ref={playbackAudioRef}
        onEnded={() => setIsPlayingRecording(false)}
        style={{ display: 'none' }}
      />
      <audio
        ref={wordAudioRef}
        onEnded={() => setIsPlayingWordAudio(false)}
        style={{ display: 'none' }}
      />

      <div className="learning-mode-header">
        <h2 className="learning-mode-header__title">{t('shadowing.pageTitle')}</h2>
        <div className="learning-mode-header__nav">
          <button
            className="sentence-nav-btn"
            onClick={() => { if (currentIndex > 0) { setCurrentIndex(currentIndex - 1); resetState(); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
            disabled={currentIndex === 0}
            aria-label={t('navigation.previousSentence')}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {segments.length}</span>
          <button
            className="sentence-nav-btn"
            onClick={handleNext}
            disabled={isLastSentence}
            aria-label={t('navigation.nextSentence')}
          >
            ▶
          </button>
        </div>
      </div>

      <div className="shadowing-content">

        {/* Professional Mode Toggle (or locked ghost UI showing progress) */}
        {isProfessionalAvailable ? (
          <div className="professional-mode-section">
            <div className="professional-mode-toggle">
              <label className="toggle-label">
                <span className="toggle-text">{t('shadowing.professional.toggle')}</span>
                <div className={`toggle-switch ${isProfessionalMode ? 'active' : ''}`} onClick={toggleProfessionalMode}>
                  <div className="toggle-knob" />
                </div>
              </label>
            </div>
            {isProfessionalMode && (
              <div className="professional-mode-info">
                <p className="professional-info-text">{t('shadowing.professional.description')}</p>
                <details className="professional-details">
                  <summary>{t('shadowing.professional.learnMore')}</summary>
                  <ul className="professional-benefits">
                    <li>{t('shadowing.professional.benefit1')}</li>
                    <li>{t('shadowing.professional.benefit2')}</li>
                    <li>{t('shadowing.professional.benefit3')}</li>
                    <li>{t('shadowing.professional.benefit4')}</li>
                  </ul>
                </details>
              </div>
            )}
          </div>
        ) : !hasDismissedLockedPrompt && (
          <div className="professional-mode-section professional-mode-section--locked">
            <div className="professional-locked-header">
              <span className="professional-locked-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </span>
              <span className="professional-locked-title">{t('shadowing.locked.title')}</span>
              <button
                type="button"
                className="professional-locked-dismiss"
                onClick={dismissLockedPrompt}
                aria-label={t('shadowing.locked.dismiss', { defaultValue: '關閉' })}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <p className="professional-locked-tagline">{t('shadowing.locked.tagline', { target: unlockCefr })}</p>
            <div
              className="professional-locked-progress"
              role="progressbar"
              aria-valuenow={Math.round(Math.min(speakingScore, unlockScore) * 100)}
              aria-valuemin={0}
              aria-valuemax={Math.round(unlockScore * 100)}
            >
              <div
                className="professional-locked-progress-bar"
                style={{ width: `${Math.min(100, (speakingScore / unlockScore) * 100)}%` }}
              />
            </div>
            <div className="professional-locked-status">
              {t('shadowing.locked.progress', {
                current: speakingCefr,
                target: unlockCefr,
              })}
            </div>
          </div>
        )}

        {/* Headphone Tip Banner */}
        {isProfessionalMode && !hasSeenHeadphoneTip && (
          <div className="headphone-tip-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
            <span>{t('shadowing.headphone.tip')}</span>
            <button className="headphone-tip-dismiss" onClick={dismissHeadphoneTip}>×</button>
          </div>
        )}

        {/* Sentence Selector (when all sentences completed) */}
        {showSentenceSelector && (
          <div className="sentence-selector">
            <div className="sentence-selector-header">
              <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: '4px'}}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>{t('shadowing.sentenceSelector.header')}</span>
              <button
                className="toggle-selector-btn"
                onClick={() => setShowSentenceSelector(!showSentenceSelector)}
              >
                {showSentenceSelector ? t('shadowing.sentenceSelector.hide') : t('shadowing.sentenceSelector.show')}
              </button>
            </div>
            <div className="sentence-selector-grid">
              {segments.map((segment, idx) => {
                const sentenceScore = progressSummary?.sentenceScores?.find(s => s.sentenceIndex === idx);
                const scoreClass = sentenceScore
                  ? sentenceScore.score >= 80 ? 'score-excellent'
                    : sentenceScore.score >= 60 ? 'score-good'
                    : 'score-needs-work'
                  : '';
                return (
                  <button
                    key={idx}
                    className={`sentence-btn ${idx === currentIndex ? 'active' : ''} ${scoreClass}`}
                    onClick={() => handleJumpToSentence(idx)}
                  >
                    <span className="btn-number">{idx + 1}</span>
                    {sentenceScore && (
                      <span className="btn-score">{Math.round(sentenceScore.score)}%</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="shadowing-card">
          <div className="sentence-display">
            {/* Script visibility toggle for professional mode */}
            {isProfessionalMode && (
              <button
                className={`script-toggle-btn ${scriptVisible ? 'visible' : 'hidden'}`}
                onClick={() => setScriptVisible(v => !v)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {scriptVisible ? (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </>
                  ) : (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </>
                  )}
                </svg>
                {scriptVisible ? t('shadowing.scripted') : t('shadowing.unscripted')}
              </button>
            )}
            <div className={`sentence-text-wrapper ${!scriptVisible && isProfessionalMode ? 'text-hidden' : ''}`}>
              <p className="sentence-text"><ClickableText text={currentSegment.text} /></p>
              {currentSegment.translation && (
                <p className="sentence-translation">{currentSegment.translation}</p>
              )}
            </div>
          </div>

          <div className="audio-best-row">
            {!isProfessionalMode && (
              <AudioPlayer
                audioUrl={currentSegment.audioUrl}
                speakingWpm={courseData?.course?.speakingWpm}
                autoNaturalSpeed={user?.autoNaturalSpeed}
                speed={audioSpeed}
                onSpeedChange={setAudioSpeed}
                repeating={audioRepeating}
                onRepeatingChange={setAudioRepeating}
              />
            )}
            {currentSentenceBestScore !== undefined && (
              <div className="current-sentence-best">
                <span className="sentence-best-label">{t('shadowing.bestScore')}</span>
                <span className={`sentence-best-score ${currentSentenceBestScore >= 80 ? 'excellent' : currentSentenceBestScore >= 60 ? 'good' : 'needs-work'}`}>
                  {Math.round(currentSentenceBestScore)}%
                </span>
              </div>
            )}
          </div>

          {/* No Audio Warning */}
          {!currentSegment.audioUrl && (
            <div className="no-audio-warning">
              <span>⚠️</span>
              <span>{t('shadowing.noAudio')}</span>
            </div>
          )}


          {/* Professional Mode: Dual Waveform UI */}
          {isProfessionalMode && !assessmentResult && !isAssessing && (
            <div className="professional-waveform-section">
              {/* System audio waveform */}
              <div className="waveform-label">{t('shadowing.professional.systemAudio')}</div>
              <div ref={wavesurferContainerRef} className="wavesurfer-container" />

              {/* Mic waveform */}
              <div className="waveform-label">{t('shadowing.professional.yourVoice')}</div>
              <canvas ref={micCanvasRef} className="mic-waveform-canvas" />

              {/* Start / Stop button */}
              <button
                className={`professional-record-btn ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopProfessionalRecording : requestProfessionalShadowing}
                disabled={!!audioBlob && !isRecording}
              >
                {isRecording ? (
                  <>
                    <span className="recording-indicator"></span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                    {' '}{t('shadowing.recording.stop')}
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                    {' '}{t('shadowing.professional.startShadowing')}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Standard Recording + Assessment Row (echoing mode) */}
          {!isProfessionalMode && (
          <div className="record-assess-section">
            {recordingError && (
              <div className="recording-error">
                <span>⚠️</span> {recordingError}
              </div>
            )}

            <div className="record-assess-row">
              {/* Record toggle button - only show when not assessing and no result */}
              {!isAssessing && !assessmentResult && (
                <div className="record-side">
                  {serviceConfigured === false ? (
                    <div className="service-not-configured">
                      ⚠️ {t('shadowing.recording.notConfigured')}
                    </div>
                  ) : (
                    <button
                      className={`record-toggle-button ${isRecording ? 'recording' : ''}`}
                      onClick={isRecording ? stopRecording : startRecording}
                      disabled={!!audioBlob && !isRecording}
                    >
                      {isRecording ? (
                        <>
                          <span className="recording-indicator"></span>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                          {' '}{t('shadowing.recording.stop')}
                        </>
                      ) : (
                        <>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                          {' '}{t('shadowing.recording.start')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Assessment Results */}
          <div className="feedback-section">

            {isAssessing && (
              <div className="assessing">
                <div className="assessing-spinner"></div>
                <p>{t('shadowing.analyzing')}</p>
              </div>
            )}

            {assessmentError && (
              <div className="assessment-error">
                <p>⚠️ {assessmentError}</p>
                <button className="retry-button" onClick={handleAssess}>
                  {t('shadowing.tryAgain')}
                </button>
              </div>
            )}

            {assessmentResult && (() => {
              return (
              <div className="assessment-result">
                <PronunciationResultPanel
                  result={{
                    overallScore: assessmentResult.overallScore,
                    accuracyScore: assessmentResult.accuracyScore,
                    fluencyScore: assessmentResult.fluencyScore,
                    completenessScore: assessmentResult.completenessScore,
                    prosodyScore: assessmentResult.prosodyScore,
                    words: assessmentResult.words,
                  }}
                  onWordPractice={handleWordPractice}
                  improvedWordIndices={new Set(updatedWordScores.keys())}
                  practiceLocked={!!wordPracticeMode}
                />

                {assessmentResult.coach && (
                  <CoachingPanel
                    result={assessmentResult.coach}
                    score={assessmentResult.overallScore}
                  />
                )}

                {/* Word Practice Mode — shared modal across speaking surfaces. */}
                {wordPracticeMode && (
                  <WordPracticeModal
                    word={wordPracticeMode.word}
                    originalScore={wordPracticeMode.originalScore}
                    phonetic={wordPhonetic}
                    audioUrl={wordAudioUrl}
                    isRecording={isRecording}
                    isAssessing={isAssessingWord}
                    isPlayingAudio={isPlayingWordAudio}
                    result={wordPracticeResult ?? undefined}
                    onStartRecord={startRecording}
                    onStopRecord={stopRecording}
                    onPlayAudio={handlePlayWordAudio}
                    onRetry={handleRetryWordPractice}
                    onCancel={handleCancelWordPractice}
                  />
                )}

                {/* Pronunciation suggestions for weak words */}
                {/* Per-word "想唸 / 聽到" suggestion block removed —
                    Loopy 教練 above already surfaces the same
                    information in conversational form, no need to
                    show it twice. */}

                {/* Collocation guidance */}
                {guidance.length > 0 && !wordPracticeMode && (
                  <DictationGuidancePanel items={guidance} />
                )}

                {/* Speaking Strategy: Show after assessment when chunks available */}
                {currentSegment.chunks.length > 0 && !wordPracticeMode && (
                  <div className="speaking-strategy-section">
                    <div className="speaking-strategy-title">{t('shadowing.speakingStrategy.title')}</div>
                    <AnnotatedChunks
                      chunks={currentSegment.chunks}
                      tone={currentSegment.tone}
                      speakingTip={currentSegment.speakingTip}
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="assessment-actions">
                  <button
                    className="try-again-button"
                    onClick={() => {
                      resetState();
                    }}
                  >
                    {t('shadowing.tryAgain')}
                  </button>
                  <button
                    className="next-sentence-button"
                    onClick={handleNext}
                  >
                    {isLastSentence ? `✓ ${t('shadowing.navigation.finish')}` : `${t('shadowing.navigation.next')} →`}
                  </button>
                </div>
              </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Course Summary Modal */}
      {showCourseSummary && (
        <div className="course-summary-overlay">
          <div className="course-summary-modal">
            <h2>{t('shadowing.summary.title')}</h2>
            <p className="summary-subtitle">{t('shadowing.summary.subtitle')}</p>

            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-value">{progressSummary?.completedCount || 0}</span>
                <span className="stat-label">{t('shadowing.summary.completedCount')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{segments.length}</span>
                <span className="stat-label">{t('shadowing.summary.totalCount')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  {progressSummary?.bestScore ? Math.round(progressSummary.bestScore) : 0}%
                </span>
                <span className="stat-label">{t('shadowing.summary.averageScore')}</span>
              </div>
            </div>

            {progressSummary?.sentenceScores && progressSummary.sentenceScores.length > 0 && (
              <div className="sentence-scores-summary">
                <h3>{t('shadowing.summary.sentenceScores')}</h3>
                <div className="scores-grid">
                  {segments.map((_, idx) => {
                    const scoreEntry = progressSummary.sentenceScores?.find(s => s.sentenceIndex === idx);
                    const score = scoreEntry?.score;
                    return (
                      <div
                        key={idx}
                        className={`score-item ${score !== undefined ? (score >= 80 ? 'excellent' : score >= 60 ? 'good' : 'needs-work') : 'not-attempted'}`}
                      >
                        <span className="sentence-num">{idx + 1}</span>
                        <span className="sentence-score">
                          {score !== undefined ? `${Math.round(score)}%` : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="summary-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowCourseSummary(false);
                  setCurrentIndex(0);
                  resetState();
                }}
              >
                <span>{t('shadowing.summary.restart')}</span>
              </button>
              <button
                className="btn-primary"
                onClick={() => setShowCourseSummary(false)}
              >
                <span>{t('shadowing.summary.close')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <XpToastContainer toasts={xpToasts} />
      <HeadphoneCheckModal
        isOpen={hpCheckOpen}
        status={hpStatus}
        detectedLabel={hpLabel}
        onConfirm={handleHpConfirm}
        onCancel={handleHpCancel}
      />
      <LeakDetectedModal
        isOpen={leakModalOpen}
        onRestart={handleLeakRestart}
        onCancel={handleLeakCancel}
      />
    </div>
  );
};

export default ShadowingPage;
